import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Eye,
  Calendar,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  X,
  Building2,
  Link as LinkIcon,
  Upload,
  Paperclip,
  FileSpreadsheet,
  ChevronDown
} from 'lucide-react';
import { contractService } from '../../../services/contractService';
import { supplierService } from '../../../services/supplierService';
import { exportToCSV, exportToExcel, formatCurrencyForExport, getDateString } from '../../../utils/exportUtils';
import { useDateFormat } from '../../../hooks/useDateFormat';

const Contracts = () => {
  const { t } = useTranslation();
  const [contracts, setContracts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [editingContract, setEditingContract] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [viewingContract, setViewingContract] = useState(null);
  const { formatDate } = useDateFormat();

  // Export handler
  const handleExport = (format) => {
    const data = contracts.map(c => ({
      contractNumber: c.contractNumber || '',
      title: c.title || '',
      supplierName: c.supplierName || '',
      contractType: c.contractType || '',
      startDate: c.startDate || '',
      endDate: c.endDate || '',
      value: formatCurrencyForExport(c.value),
      currency: c.currency || 'USD',
      status: c.status || '',
      autoRenew: c.autoRenew ? 'Yes' : 'No',
      linkedAssets: c.assets?.length || 0,
    }));

    const columns = [
      { key: 'contractNumber', label: 'Contract Number' },
      { key: 'title', label: 'Title' },
      { key: 'supplierName', label: 'Supplier' },
      { key: 'contractType', label: 'Type' },
      { key: 'startDate', label: 'Start Date' },
      { key: 'endDate', label: 'End Date' },
      { key: 'value', label: 'Value' },
      { key: 'currency', label: 'Currency' },
      { key: 'status', label: 'Status' },
      { key: 'autoRenew', label: 'Auto Renew' },
      { key: 'linkedAssets', label: 'Linked Assets' },
    ];

    const filename = `contracts-${getDateString()}`;
    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename);
    }
    setShowExportMenu(false);
  };
  const [formData, setFormData] = useState({
    supplierId: '',
    contractNumber: '',
    title: '',
    description: '',
    contractType: 'FULL_SERVICE',
    startDate: '',
    endDate: '',
    value: '',
    currency: 'USD',
    status: 'DRAFT',
    terms: '',
    autoRenew: false,
    renewalNoticeDays: 30,
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    assetIds: []
  });

  // Mock data
  const mockContracts = [
    {
      id: 1,
      contractNumber: 'MC-2024-001',
      title: 'Annual HVAC Maintenance',
      supplierId: 1,
      supplierName: 'TechMaint Solutions',
      supplierCode: 'SUP-001',
      contractType: 'FULL_SERVICE',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      value: 45000,
      currency: 'USD',
      status: 'ACTIVE',
      autoRenew: true,
      assets: [
        { id: 1, assetName: 'HVAC Unit 1', assetTag: 'HVAC-001' },
        { id: 2, assetName: 'HVAC Unit 2', assetTag: 'HVAC-002' }
      ]
    },
    {
      id: 2,
      contractNumber: 'MC-2024-002',
      title: 'Generator Maintenance Contract',
      supplierId: 2,
      supplierName: 'Global Parts Inc',
      supplierCode: 'SUP-002',
      contractType: 'PARTS_ONLY',
      startDate: '2024-03-01',
      endDate: '2025-02-28',
      value: 25000,
      currency: 'USD',
      status: 'ACTIVE',
      autoRenew: false,
      assets: [
        { id: 3, assetName: 'Generator A', assetTag: 'GEN-001' }
      ]
    },
    {
      id: 3,
      contractNumber: 'MC-2024-003',
      title: 'Quarterly Equipment Inspection',
      supplierId: 3,
      supplierName: 'Industrial Equipment Co',
      supplierCode: 'SUP-003',
      contractType: 'INSPECTION',
      startDate: '2024-01-01',
      endDate: '2024-06-30',
      value: 8000,
      currency: 'USD',
      status: 'EXPIRED',
      autoRenew: false,
      assets: []
    },
    {
      id: 4,
      contractNumber: 'MC-2024-004',
      title: 'Elevator Service Agreement',
      supplierId: 1,
      supplierName: 'TechMaint Solutions',
      supplierCode: 'SUP-001',
      contractType: 'FULL_SERVICE',
      startDate: '2024-06-01',
      endDate: '2025-05-31',
      value: 35000,
      currency: 'USD',
      status: 'DRAFT',
      autoRenew: true,
      assets: []
    }
  ];

  const mockSuppliers = [
    { id: 1, code: 'SUP-001', name: 'TechMaint Solutions' },
    { id: 2, code: 'SUP-002', name: 'Global Parts Inc' },
    { id: 3, code: 'SUP-003', name: 'Industrial Equipment Co' }
  ];

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contractsData, suppliersData] = await Promise.all([
        contractService.getContracts(),
        supplierService.getSuppliers()
      ]);
      setContracts(contractsData || []);
      setSuppliers(suppliersData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      // Fallback to mock data for development
      setContracts(mockContracts);
      setSuppliers(mockSuppliers);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: { bg: 'bg-gray-100 text-gray-800', icon: Clock },
      ACTIVE: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
      EXPIRED: { bg: 'bg-red-100 text-red-800', icon: AlertTriangle },
      CANCELLED: { bg: 'bg-orange-100 text-orange-800', icon: X }
    };
    return styles[status] || styles.DRAFT;
  };

  const getStatusLabel = (status) => {
    const map = {
      DRAFT: t('cmms.con_statusDraft'),
      ACTIVE: t('common.active'),
      EXPIRED: t('cmms.con_statusExpired'),
      CANCELLED: t('cmms.wo_cancelled')
    };
    return map[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = {
      FULL_SERVICE: t('cmms.con_typeFullService'),
      PARTS_ONLY: t('cmms.con_typePartsOnly'),
      LABOR_ONLY: t('cmms.con_typeLaborOnly'),
      INSPECTION: t('cmms.con_typeInspection')
    };
    return labels[type] || type;
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesStatus = filterStatus === 'all' || contract.status === filterStatus;
    const matchesSearch =
      contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.contractNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const handleCreate = () => {
    setEditingContract(null);
    setFormData({
      supplierId: '',
      contractNumber: '',
      title: '',
      description: '',
      contractType: 'FULL_SERVICE',
      startDate: '',
      endDate: '',
      value: '',
      currency: 'USD',
      status: 'DRAFT',
      terms: '',
      autoRenew: false,
      renewalNoticeDays: 30,
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      assetIds: []
    });
    setFilesToUpload([]);
    setExistingFiles([]);
    setShowModal(true);
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      supplierId: contract.supplierId,
      contractNumber: contract.contractNumber,
      title: contract.title,
      description: contract.description || '',
      contractType: contract.contractType,
      startDate: contract.startDate,
      endDate: contract.endDate,
      value: contract.value,
      currency: contract.currency,
      status: contract.status,
      terms: contract.terms || '',
      autoRenew: contract.autoRenew,
      renewalNoticeDays: contract.renewalNoticeDays || 30,
      contactPerson: contract.contactPerson || '',
      contactEmail: contract.contactEmail || '',
      contactPhone: contract.contactPhone || '',
      assetIds: contract.assets?.map(a => a.id) || []
    });
    setFilesToUpload([]);
    setExistingFiles(contract.files || []);
    setShowModal(true);
  };

  const handleAddFile = (e) => {
    const files = Array.from(e.target.files);
    setFilesToUpload(prev => [...prev, ...files.map(file => ({ file, description: '' }))]);
    e.target.value = ''; // Reset input
  };

  const handleRemoveNewFile = (index) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileDescriptionChange = (index, description) => {
    setFilesToUpload(prev => prev.map((item, i) =>
      i === index ? { ...item, description } : item
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let contractId;
      if (editingContract) {
        await contractService.updateContract(editingContract.id, formData);
        contractId = editingContract.id;
      } else {
        const newContract = await contractService.createContract(formData);
        contractId = newContract.id;
      }

      // Upload any new files
      if (filesToUpload.length > 0 && contractId) {
        for (const fileItem of filesToUpload) {
          try {
            await contractService.uploadFile(contractId, fileItem.file, fileItem.description);
          } catch (fileError) {
            console.error('Error uploading file:', fileItem.file.name, fileError);
          }
        }
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Failed to save contract. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      try {
        await contractService.deleteContract(id);
        loadData();
      } catch (error) {
        console.error('Error deleting contract:', error);
      }
    }
  };

  const handleUpload = (contract) => {
    setSelectedContract(contract);
    setUploadFile(null);
    setUploadDescription('');
    setShowUploadModal(true);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedContract) return;

    try {
      await contractService.uploadFile(selectedContract.id, uploadFile, uploadDescription);
      setShowUploadModal(false);
      loadData();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    }
  };

  const handleDeleteFile = async (contractId, fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await contractService.deleteFile(contractId, fileId);
        loadData();
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file. Please try again.');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const getDaysUntilExpiry = (endDate) => {
    const end = new Date(endDate);
    const today = new Date();
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Stats
  const stats = {
    total: contracts.length,
    active: contracts.filter(c => c.status === 'ACTIVE').length,
    expiringSoon: contracts.filter(c => {
      const days = getDaysUntilExpiry(c.endDate);
      return c.status === 'ACTIVE' && days <= 30 && days > 0;
    }).length,
    totalValue: contracts.filter(c => c.status === 'ACTIVE').reduce((sum, c) => sum + c.value, 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.contracts')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.contracts_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="w-5 h-5 mr-2" />
              {t('common.export')}
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    {t('common.exportExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    {t('common.exportCsv')}
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('common.create')} {t('cmms.contracts')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('common.total')} {t('cmms.contracts')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.active}</div>
              <div className="text-sm text-gray-600">{t('common.active')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.expiringSoon}</div>
              <div className="text-sm text-gray-600">{t('cmms.con_expiringSoon')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalValue)}</div>
              <div className="text-sm text-gray-600">{t('cmms.con_activeValue')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('common.search') + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="DRAFT">{t('cmms.con_statusDraft')}</option>
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="EXPIRED">{t('cmms.con_statusExpired')}</option>
            <option value="CANCELLED">{t('cmms.wo_cancelled')}</option>
          </select>
        </div>
      </div>

      {/* Contracts List */}
      <div className="space-y-4">
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('common.loading')}...</p>
          </div>
        )}

        {!loading && filteredContracts.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.noResults')}</h3>
            <p className="text-gray-500 mb-4">{t('cmms.contracts_emptyState')}</p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('common.create')} {t('cmms.contracts')}
            </button>
          </div>
        )}

        {filteredContracts.map((contract) => {
          const statusStyle = getStatusBadge(contract.status);
          const StatusIcon = statusStyle.icon;
          const daysUntilExpiry = getDaysUntilExpiry(contract.endDate);
          const isExpiringSoon = contract.status === 'ACTIVE' && daysUntilExpiry <= 30 && daysUntilExpiry > 0;

          return (
            <div key={contract.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{contract.title}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${statusStyle.bg}`}>
                        <StatusIcon className="w-3 h-3" />
                        {getStatusLabel(contract.status)}
                      </span>
                      {isExpiringSoon && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          <AlertTriangle className="w-3 h-3" />
                          {t('cmms.con_daysLeft', { count: daysUntilExpiry })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{contract.contractNumber}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {contract.supplierName}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(contract.startDate)} - {formatDate(contract.endDate)}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {formatCurrency(contract.value, contract.currency)}
                      </span>
                    </div>
                    {contract.assets && contract.assets.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <LinkIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {contract.assets.length} {t('cmms.con_linkedAssets')}
                        </span>
                        <div className="flex gap-1">
                          {contract.assets.slice(0, 3).map(asset => (
                            <span key={asset.id} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              {asset.assetTag}
                            </span>
                          ))}
                          {contract.assets.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              {t('cmms.con_moreAssets', { count: contract.assets.length - 3 })}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                    {getTypeLabel(contract.contractType)}
                  </span>
                  {contract.autoRenew && (
                    <span className="px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full">
                      {t('cmms.con_autoRenew')}
                    </span>
                  )}
                  <button
                    onClick={() => handleUpload(contract)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Upload document"
                  >
                    <Upload className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewingContract(contract)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title={t('common.view')}
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleEdit(contract)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(contract.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Files Section */}
              {contract.files && contract.files.length > 0 && (
                <div className="border-t border-gray-100 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t('cmms.con_documents')} ({contract.files.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {contract.files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group"
                      >
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{file.fileName}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(file.fileSize)})</span>
                        <button
                          onClick={() => handleDeleteFile(contract.id, file.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* View Contract Modal */}
      {viewingContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{t('common.view')} {t('cmms.contracts')}</h2>
              <button onClick={() => setViewingContract(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('cmms.con_contractNumber')}</p>
                  <p className="mt-1 text-sm text-gray-900">{viewingContract.contractNumber || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('common.status')}</p>
                  <div className="mt-1">
                    {(() => {
                      const s = getStatusBadge(viewingContract.status);
                      const SI = s.icon;
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${s.bg}`}>
                          <SI className="w-3 h-3" />
                          {getStatusLabel(viewingContract.status)}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('cmms.con_title')}</p>
                  <p className="mt-1 text-sm text-gray-900">{viewingContract.title || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('cmms.con_supplier')}</p>
                  <p className="mt-1 text-sm text-gray-900">{viewingContract.supplierName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('common.type')}</p>
                  <p className="mt-1 text-sm text-gray-900">{getTypeLabel(viewingContract.contractType)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('cmms.con_startDate')}</p>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(viewingContract.startDate) || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('cmms.con_endDate')}</p>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(viewingContract.endDate) || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('cmms.con_value')}</p>
                  <p className="mt-1 text-sm text-gray-900">{formatCurrency(viewingContract.value, viewingContract.currency)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('cmms.con_autoRenew')}</p>
                  <p className="mt-1 text-sm text-gray-900">{viewingContract.autoRenew ? t('common.yes') : t('common.no')}</p>
                </div>
              </div>

              {/* Description */}
              {viewingContract.description && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('common.description')}</p>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{viewingContract.description}</p>
                </div>
              )}

              {/* Linked Assets */}
              {viewingContract.assets && viewingContract.assets.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('cmms.con_linkedAssets')}</p>
                  <div className="flex flex-wrap gap-2">
                    {viewingContract.assets.map(asset => (
                      <span key={asset.id} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-lg">
                        <LinkIcon className="w-3 h-3" />
                        {asset.assetName} ({asset.assetTag})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Attached Files */}
              {viewingContract.files && viewingContract.files.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('cmms.con_documents')} ({viewingContract.files.length})</p>
                  <div className="space-y-2">
                    {viewingContract.files.map(file => (
                      <div key={file.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{file.fileName}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(file.fileSize)})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setViewingContract(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {t('common.close')}
              </button>
              <button
                onClick={() => { handleEdit(viewingContract); setViewingContract(null); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('common.edit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingContract ? `${t('common.edit')} ${t('cmms.contracts')}` : `${t('common.create')} ${t('cmms.contracts')}`}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_contractNumber')} *</label>
                  <input
                    type="text"
                    value={formData.contractNumber}
                    onChange={(e) => setFormData({...formData, contractNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_supplier')} *</label>
                  <select
                    value={formData.supplierId}
                    onChange={(e) => setFormData({...formData, supplierId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">{t('cmms.con_selectSupplier')}</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_title')} *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.type')} *</label>
                  <select
                    value={formData.contractType}
                    onChange={(e) => setFormData({...formData, contractType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="FULL_SERVICE">{t('cmms.con_typeFullService')}</option>
                    <option value="PARTS_ONLY">{t('cmms.con_typePartsOnly')}</option>
                    <option value="LABOR_ONLY">{t('cmms.con_typeLaborOnly')}</option>
                    <option value="INSPECTION">{t('cmms.con_typeInspection')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="DRAFT">{t('cmms.con_statusDraft')}</option>
                    <option value="ACTIVE">{t('common.active')}</option>
                    <option value="EXPIRED">{t('cmms.con_statusExpired')}</option>
                    <option value="CANCELLED">{t('cmms.wo_cancelled')}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_startDate')} *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_endDate')} *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_value')}</label>
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({...formData, value: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_currency')}</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.autoRenew}
                    onChange={(e) => setFormData({...formData, autoRenew: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">{t('cmms.con_autoRenewContract')}</span>
                </label>
              </div>

              {/* Document Attachments Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t('cmms.con_contractDocuments')}
                  </h3>
                  <label className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer text-sm">
                    <Upload className="w-4 h-4 mr-1" />
                    {t('cmms.con_addFile')}
                    <input
                      type="file"
                      onChange={handleAddFile}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      multiple
                    />
                  </label>
                </div>

                {/* Existing Files (when editing) */}
                {existingFiles.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">{t('cmms.con_existingDocuments')}</p>
                    <div className="space-y-2">
                      {existingFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-gray-700">{file.fileName}</span>
                            <span className="text-xs text-gray-400">({formatFileSize(file.fileSize)})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteFile(editingContract.id, file.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Files to Upload */}
                {filesToUpload.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">{t('cmms.con_filesToUpload')}</p>
                    <div className="space-y-2">
                      {filesToUpload.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 truncate">{item.file.name}</p>
                            <input
                              type="text"
                              placeholder={t('cmms.con_fileDescriptionPh')}
                              value={item.description}
                              onChange={(e) => handleFileDescriptionChange(index, e.target.value)}
                              className="w-full mt-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveNewFile(index)}
                            className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {existingFiles.length === 0 && filesToUpload.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    {t('cmms.con_noDocuments')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? `${t('common.save')}...` : (editingContract ? `${t('common.update')} ${t('cmms.contracts')}` : `${t('common.create')} ${t('cmms.contracts')}`)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{t('cmms.con_uploadDocument')}</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleFileUpload} className="p-6 space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  {t('cmms.con_uploadingTo')} <strong>{selectedContract?.title}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.con_fileLabel')} *</label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('cmms.con_supportedFormats')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <input
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Signed contract copy, Amendment #1"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('cmms.con_upload')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
